// src/components/ProjectSelectionView.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FolderOpen, Clock, Plus, ChevronDown, ChevronUp, Info, MoreHorizontal, SquarePen, Trash2, X, Check } from 'lucide-react';
import { getCookie } from '../utils/utils';
import { useCaps } from '../contexts/CapabilitiesContext';

interface ProjectMember {
  id: number;
  user: number;
  user_email: string;
  user_name: string;
  role: string;
  joined_at: string;
}

interface Project {
  id: number;
  name: string;
  goal?: string;
  category?: string;
  markets?: string[];
  territory_demand_space?: string;
  occasion?: string;
  opportunity_space?: string;
  score?: number;
  owner?: number;
  owner_email?: string;
  members?: ProjectMember[];
  member_count?: number;
  is_owner?: boolean;
  blueprint_count?: number;
  created_at?: string;
  team?: number | null;
  team_name?: string | null;
  gather_project_info?: boolean;
  status?: 'active' | 'in_process' | 'outputs_ready' | 'jobs_identifier_creating';
  lifecycle?: 'draft' | 'active';
  max_job_score?: number;
  max_platform_score?: number;
  [key: string]: unknown;
}

interface ProjectSelectionViewProps {
  onProjectSelect: (projectId: number) => void;
  onViewGuaranteedOutputs?: (projectId: number) => void;
  onCreateProject: () => void;
  selectedTeamFilter: number | null;
  setSelectedTeamFilter: (value: number | null) => void;
  selectedMarketFilter: string;
  setSelectedMarketFilter: (value: string) => void;
  selectedTerritoryFilter: string;
  setSelectedTerritoryFilter: (value: string) => void;
  selectedCategoryFilter: string;
  setSelectedCategoryFilter: (value: string) => void;
  selectedOccasionFilter: string;
  setSelectedOccasionFilter: (value: string) => void;
  onProjectDeleted?: (projectId: number) => void;
  onProjectRenamed?: () => void;
  refreshTrigger?: number;
}

type SortOption = 'jobs-prioritiser' | 'platform-prioritiser' | 'newest' | 'oldest';

type FilterSectionKey = 'brand' | 'format' | 'market' | 'date' | 'addedBy';

interface AddedByOption {
  value: string;
  label: string;
}

interface ProjectFilterOptions {
  markets: string[];
  territory_demand_space: string[];
  categories: string[];
  occasions: string[];
  brands: string[];
  formats: string[];
  added_by: AddedByOption[];
}

const FILTER_VISIBLE_COUNT = 3;

const headerControlClass =
  'inline-flex items-center gap-2 pl-4 pr-3 py-2 bg-white border border-[#E0DEE8] rounded-lg text-sm text-gray-700 cursor-pointer hover:border-[#655FA8]/30 focus:outline-none focus:ring-2 focus:ring-[#655FA8]/30 focus:border-[#655FA8]/40';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'jobs-prioritiser', label: 'Jobs Prioritiser' },
  { value: 'platform-prioritiser', label: 'Platform Prioritiser' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];

function toggleInList(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

interface FilterCheckboxSectionProps {
  title: string;
  items: string[];
  selected: string[];
  expanded: boolean;
  showAll: boolean;
  onToggleExpanded: () => void;
  onToggleItem: (value: string) => void;
  onToggleShowAll: () => void;
}

const FilterCheckboxSection: React.FC<FilterCheckboxSectionProps> = ({
  title,
  items,
  selected,
  expanded,
  showAll,
  onToggleExpanded,
  onToggleItem,
  onToggleShowAll,
}) => {
  const visibleItems = showAll ? items : items.slice(0, FILTER_VISIBLE_COUNT);
  const hiddenCount = Math.max(0, items.length - FILTER_VISIBLE_COUNT);

  return (
    <div className="border-b border-[#EEEEEE] last:border-b-0">
      <button
        type="button"
        onClick={onToggleExpanded}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-sm font-bold text-gray-900">{title}</span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
        )}
      </button>
      {expanded && items.length > 0 ? (
        <div className="px-4 pb-4 space-y-3">
          {visibleItems.map((item) => (
            <label key={item} className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
              <input
                type="checkbox"
                checked={selected.includes(item)}
                onChange={() => onToggleItem(item)}
                className="h-4 w-4 rounded border-[#D1D1D1] text-[#655FA8] focus:ring-[#655FA8]/30"
              />
              <span>{item}</span>
            </label>
          ))}
          {!showAll && hiddenCount > 0 ? (
            <button
              type="button"
              onClick={onToggleShowAll}
              className="text-sm font-medium text-[#43C8F5] hover:text-[#2A9CC4]"
            >
              + {hiddenCount} more
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const ProjectSelectionView: React.FC<ProjectSelectionViewProps> = ({
  onProjectSelect,
  onViewGuaranteedOutputs,
  onCreateProject,
  selectedTeamFilter,
  selectedMarketFilter,
  selectedTerritoryFilter,
  selectedCategoryFilter,
  selectedOccasionFilter,
  onProjectDeleted,
  onProjectRenamed,
  refreshTrigger = 0,
}) => {
  const { can } = useCaps();
  const canViewGos = can('gos_view');
  const canCreateProject = can('projects_create');
  const canEditProject = can('projects_edit');
  const canViewProjects = can('projects_view');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [openMenuProjectId, setOpenMenuProjectId] = useState<number | null>(null);
  const [renameProject, setRenameProject] = useState<Project | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; project: Project | null }>({
    show: false,
    project: null,
  });
  const [deleting, setDeleting] = useState(false);
  const [filterOptions, setFilterOptions] = useState<ProjectFilterOptions>({
    markets: [],
    territory_demand_space: [],
    categories: [],
    occasions: [],
    brands: [],
    formats: [],
    added_by: [],
  });
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [selectedFilterMarkets, setSelectedFilterMarkets] = useState<string[]>([]);
  const [selectedAddedBy, setSelectedAddedBy] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedFilterSections, setExpandedFilterSections] = useState<Record<FilterSectionKey, boolean>>({
    brand: true,
    format: true,
    market: true,
    date: true,
    addedBy: true,
  });
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllFormats, setShowAllFormats] = useState(false);
  const [showAllMarkets, setShowAllMarkets] = useState(false);
  const [showAllAddedBy, setShowAllAddedBy] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showSortPanel, setShowSortPanel] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const sortPanelRef = useRef<HTMLDivElement>(null);

  const buildProjectParams = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedTeamFilter) params.set('team_id', String(selectedTeamFilter));
    if (selectedMarketFilter) params.set('market', selectedMarketFilter);
    if (selectedTerritoryFilter) params.set('territory_demand_space', selectedTerritoryFilter);
    if (selectedCategoryFilter) params.set('category', selectedCategoryFilter);
    if (selectedOccasionFilter) params.set('occasion', selectedOccasionFilter);
    selectedBrands.forEach((brand) => params.append('brand', brand));
    selectedFormats.forEach((format) => params.append('format', format));
    selectedFilterMarkets.forEach((market) => params.append('filter_market', market));
    selectedAddedBy.forEach((ownerId) => params.append('added_by', ownerId));
    if (dateFrom) params.set('created_from', dateFrom);
    if (dateTo) params.set('created_to', dateTo);
    params.set('lifecycle', 'active');
    return params.toString();
  }, [
    selectedTeamFilter,
    selectedMarketFilter,
    selectedTerritoryFilter,
    selectedCategoryFilter,
    selectedOccasionFilter,
    selectedBrands,
    selectedFormats,
    selectedFilterMarkets,
    selectedAddedBy,
    dateFrom,
    dateTo,
  ]);

  const fetchProjects = async (isInitial = false) => {
    if (isInitial) {
      setIsInitialLoading(true);
    }
    try {
      const qs = buildProjectParams();
      const url = `/api/projects/${qs ? `?${qs}` : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProjects(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      if (isInitial) {
        setIsInitialLoading(false);
      }
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const qs = buildProjectParams();
      const url = `/api/projects/filter-options/${qs ? `?${qs}` : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFilterOptions({
          markets: Array.isArray(data.markets) ? data.markets : [],
          territory_demand_space: Array.isArray(data.territory_demand_space) ? data.territory_demand_space : [],
          categories: Array.isArray(data.categories) ? data.categories : [],
          occasions: Array.isArray(data.occasions) ? data.occasions : [],
          brands: Array.isArray(data.brands) ? data.brands : [],
          formats: Array.isArray(data.formats) ? data.formats : [],
          added_by: Array.isArray(data.added_by) ? data.added_by : [],
        });
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchProjects(true);
  }, []);

  useEffect(() => {
    if (refreshTrigger === 0) return;
    fetchProjects(false);
    fetchFilterOptions();
  }, [refreshTrigger]);

  useEffect(() => {
    fetchProjects(false);
    fetchFilterOptions();
  }, [
    selectedTeamFilter,
    selectedMarketFilter,
    selectedTerritoryFilter,
    selectedCategoryFilter,
    selectedOccasionFilter,
    selectedBrands,
    selectedFormats,
    selectedFilterMarkets,
    selectedAddedBy,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    if (openMenuProjectId === null) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-project-card-menu]')) {
        setOpenMenuProjectId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenuProjectId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showFilterPanel && filterPanelRef.current && !filterPanelRef.current.contains(target)) {
        setShowFilterPanel(false);
      }
      if (showSortPanel && sortPanelRef.current && !sortPanelRef.current.contains(target)) {
        setShowSortPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterPanel, showSortPanel]);

  const sortedProjects = useMemo(() => {
    const list = [...projects];
    switch (sortBy) {
      case 'jobs-prioritiser':
        return list.sort(
          (a, b) => (b.max_job_score ?? b.score ?? 0) - (a.max_job_score ?? a.score ?? 0)
        );
      case 'platform-prioritiser':
        return list.sort(
          (a, b) => (b.max_platform_score ?? 0) - (a.max_platform_score ?? 0)
        );
      case 'oldest':
        return list.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
      case 'newest':
      default:
        return list.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
    }
  }, [projects, sortBy]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const toggleFilterSection = (key: FilterSectionKey) => {
    setExpandedFilterSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const activeFilterCount =
    selectedBrands.length +
    selectedFormats.length +
    selectedFilterMarkets.length +
    selectedAddedBy.length +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0);

  const hasActiveFilters =
    selectedTeamFilter ||
    selectedMarketFilter ||
    selectedTerritoryFilter ||
    selectedCategoryFilter ||
    selectedOccasionFilter ||
    activeFilterCount > 0;

  const sortLabel = SORT_OPTIONS.find((option) => option.value === sortBy)?.label ?? 'Sort';

  const openRenameModal = (project: Project) => {
    setOpenMenuProjectId(null);
    setRenameProject(project);
    setRenameValue(project.name);
    setRenameError(null);
  };

  const closeRenameModal = () => {
    if (renameSaving) return;
    setRenameProject(null);
    setRenameValue('');
    setRenameError(null);
  };

  const handleRenameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renameProject) return;

    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenameError('Project name is required.');
      return;
    }

    setRenameSaving(true);
    setRenameError(null);
    try {
      const response = await fetch(`/api/projects/${renameProject.id}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken') || '',
        },
        body: JSON.stringify({ name: trimmed }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message =
          errorData.detail ||
          errorData.error ||
          (typeof errorData === 'object' ? Object.values(errorData).flat().join(', ') : '') ||
          'Failed to rename project';
        throw new Error(typeof message === 'string' ? message : 'Failed to rename project');
      }

      const updated = await response.json();
      setProjects((prev) =>
        prev.map((p) => (p.id === renameProject.id ? { ...p, ...updated } : p))
      );
      onProjectRenamed?.();
      setRenameProject(null);
      setRenameValue('');
      setRenameError(null);
    } catch (err: unknown) {
      setRenameError(err instanceof Error ? err.message : 'Failed to rename project');
    } finally {
      setRenameSaving(false);
    }
  };

  const openDeleteModal = (project: Project) => {
    setOpenMenuProjectId(null);
    setDeleteConfirm({ show: true, project });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.project) return;

    setDeleting(true);
    try {
      const response = await fetch(`/api/projects/${deleteConfirm.project.id}/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken') || '',
        },
      });

      if (response.ok) {
        const deletedId = deleteConfirm.project.id;
        setProjects((prev) => prev.filter((p) => p.id !== deletedId));
        setDeleteConfirm({ show: false, project: null });
        onProjectDeleted?.(deletedId);
      } else {
        console.error('Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setDeleting(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-full p-8 bg-[#F9F9F9]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#655FA8] mx-auto" />
          <p className="mt-4 text-gray-500">Loading your projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto flex flex-col">
      <div className="bg-[#F9F9F9] px-8 py-6 border-b border-[#EEEEEE]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">Your Projects</h1>
            <p className="text-sm text-gray-500 mt-1">
              Select a project to view its blueprints, or create a new one
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <div className="relative" ref={filterPanelRef}>
              <button
                type="button"
                onClick={() => {
                  setShowFilterPanel((open) => !open);
                  setShowSortPanel(false);
                }}
                className={`${headerControlClass} min-w-[100px] justify-between`}
                aria-expanded={showFilterPanel}
                aria-haspopup="dialog"
              >
                <span>
                  Filter
                  {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
              </button>

              {showFilterPanel ? (
                <div className="absolute right-0 top-full mt-2 z-30 w-[320px] max-h-[70vh] overflow-y-auto rounded-xl border border-[#E0DEE8] bg-white shadow-xl">
                  <FilterCheckboxSection
                    title="Brand"
                    items={filterOptions.brands}
                    selected={selectedBrands}
                    expanded={expandedFilterSections.brand}
                    showAll={showAllBrands}
                    onToggleExpanded={() => toggleFilterSection('brand')}
                    onToggleItem={(value) => setSelectedBrands((prev) => toggleInList(prev, value))}
                    onToggleShowAll={() => setShowAllBrands(true)}
                  />
                  <FilterCheckboxSection
                    title="Format"
                    items={filterOptions.formats}
                    selected={selectedFormats}
                    expanded={expandedFilterSections.format}
                    showAll={showAllFormats}
                    onToggleItem={(value) => setSelectedFormats((prev) => toggleInList(prev, value))}
                    onToggleExpanded={() => toggleFilterSection('format')}
                    onToggleShowAll={() => setShowAllFormats(true)}
                  />
                  <FilterCheckboxSection
                    title="Market"
                    items={filterOptions.markets}
                    selected={selectedFilterMarkets}
                    expanded={expandedFilterSections.market}
                    showAll={showAllMarkets}
                    onToggleItem={(value) => setSelectedFilterMarkets((prev) => toggleInList(prev, value))}
                    onToggleExpanded={() => toggleFilterSection('market')}
                    onToggleShowAll={() => setShowAllMarkets(true)}
                  />

                  <div className="border-b border-[#EEEEEE]">
                    <button
                      type="button"
                      onClick={() => toggleFilterSection('date')}
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                    >
                      <span className="text-sm font-bold text-gray-900">Date</span>
                      {expandedFilterSections.date ? (
                        <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
                      )}
                    </button>
                    {expandedFilterSections.date ? (
                      <div className="px-4 pb-4 flex items-center gap-2">
                        <input
                          type="date"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                          className="flex-1 min-w-0 rounded-lg border border-[#E0DEE8] px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#655FA8]/30"
                          aria-label="From date"
                        />
                        <span className="text-gray-400">-</span>
                        <input
                          type="date"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                          className="flex-1 min-w-0 rounded-lg border border-[#E0DEE8] px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#655FA8]/30"
                          aria-label="To date"
                        />
                      </div>
                    ) : null}
                  </div>

                  <div className="border-b border-[#EEEEEE] last:border-b-0">
                    <button
                      type="button"
                      onClick={() => toggleFilterSection('addedBy')}
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                    >
                      <span className="text-sm font-bold text-gray-900">Added By</span>
                      {expandedFilterSections.addedBy ? (
                        <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
                      )}
                    </button>
                    {expandedFilterSections.addedBy && filterOptions.added_by.length > 0 ? (
                      <div className="px-4 pb-4 space-y-3">
                        {(showAllAddedBy
                          ? filterOptions.added_by
                          : filterOptions.added_by.slice(0, FILTER_VISIBLE_COUNT)
                        ).map((option) => (
                          <label
                            key={option.value}
                            className="flex items-center gap-3 cursor-pointer text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={selectedAddedBy.includes(option.value)}
                              onChange={() =>
                                setSelectedAddedBy((prev) => toggleInList(prev, option.value))
                              }
                              className="h-4 w-4 rounded border-[#D1D1D1] text-[#655FA8] focus:ring-[#655FA8]/30"
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                        {!showAllAddedBy && filterOptions.added_by.length > FILTER_VISIBLE_COUNT ? (
                          <button
                            type="button"
                            onClick={() => setShowAllAddedBy(true)}
                            className="text-sm font-medium text-[#43C8F5] hover:text-[#2A9CC4]"
                          >
                            + {filterOptions.added_by.length - FILTER_VISIBLE_COUNT} more
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="relative" ref={sortPanelRef}>
              <button
                type="button"
                onClick={() => {
                  setShowSortPanel((open) => !open);
                  setShowFilterPanel(false);
                }}
                className={`${headerControlClass} min-w-[100px] justify-between`}
                aria-expanded={showSortPanel}
                aria-haspopup="listbox"
              >
                <span>{sortBy === 'newest' ? 'Sort' : sortLabel}</span>
                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
              </button>

              {showSortPanel ? (
                <div
                  className="absolute right-0 top-full mt-2 z-30 w-52 rounded-xl border border-[#E0DEE8] bg-white shadow-xl py-2"
                  role="listbox"
                  aria-label="Sort projects"
                >
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={sortBy === option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortPanel(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                        sortBy === option.value
                          ? 'text-gray-900 font-medium bg-gray-50'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {canCreateProject && (
              <button
                onClick={onCreateProject}
                className="inline-flex items-center gap-2.5 rounded-full border border-[#4f9dc3] bg-white px-4 py-2 text-sm font-normal text-[#4f9dc3] hover:bg-[#4f9dc3]/5 transition-colors"
              >
                <Plus className="w-4 h-4 shrink-0" />
                Add a new project
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 bg-white px-8 py-6">
        {sortedProjects.length === 0 ? (
          <div className="py-16 text-left">
            <FolderOpen className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-gray-500">
              {hasActiveFilters
                ? 'No projects match your filters.'
                : 'No projects yet. Create your first one!'}
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-5">
            {sortedProjects.map((project) => {
              const isDraft = project.lifecycle === 'draft';
              return (
              <div
                key={project.id}
                data-tooltip-anchor="project-card"
                className={`relative w-[450px] ${isDraft ? 'min-h-0' : 'min-h-[200px]'} flex flex-col bg-[#F9F9F9] rounded-xl border border-[#EEEEEE] shadow-sm transition-all group overflow-hidden ${
                  canViewProjects && !isDraft
                    ? 'hover:shadow-md hover:border-[#655FA8]/20 cursor-pointer'
                    : canViewProjects
                      ? 'hover:shadow-md hover:border-[#655FA8]/20'
                      : 'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (!canViewProjects || isDraft) return;
                  onProjectSelect(project.id);
                }}
                onKeyDown={(e) => {
                  if (!canViewProjects || isDraft) return;
                  if (e.key === 'Enter' || e.key === ' ') onProjectSelect(project.id);
                }}
                role={isDraft ? 'group' : 'button'}
                tabIndex={canViewProjects && !isDraft ? 0 : -1}
              >
                {project.is_owner && (
                  <div
                    className={`absolute top-4 right-4 z-10 ${openMenuProjectId === project.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                    data-project-card-menu
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuProjectId((current) =>
                          current === project.id ? null : project.id
                        );
                      }}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200/80 transition-colors"
                      aria-label="Project options"
                      aria-expanded={openMenuProjectId === project.id}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                    {openMenuProjectId === project.id && (
                      <div
                        className="absolute right-0 top-full mt-1 w-[108px] h-[69px] box-border flex flex-col px-3 text-sm text-black"
                        style={{
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          gap: '8px',
                          backgroundColor: '#262626',
                          backgroundImage: 'linear-gradient(rgba(245,245,245,0.67), rgba(245,245,245,0.67))',
                          borderRadius: '13px',
                          boxShadow: '0 0 25px rgba(0,0,0,0.16), 0 0 2px rgba(0,0,0,0.10)',
                        }}
                      >
                        {canEditProject && (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openRenameModal(project);
                              }}
                              className="h-[18px] shrink-0 w-full text-left font-bold hover:opacity-80 flex items-center gap-1.5 text-black leading-none"
                            >
                              <SquarePen className="w-3.5 h-3.5 shrink-0" strokeWidth={2.25} />
                              Rename
                            </button>
                            <div className="h-px shrink-0 border-t border-[#979797]" />
                          </>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(project);
                          }}
                          className="h-[18px] shrink-0 w-full text-left font-bold text-black hover:opacity-80 flex items-center gap-1.5 leading-none"
                        >
                          <Trash2 className="w-3.5 h-3.5 shrink-0" strokeWidth={2.25} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex-1 min-h-0 w-full text-left p-5 flex flex-col">
                  <div className={`${isDraft ? '' : 'mb-2 pr-8'} shrink-0`}>
                    <div className="flex items-center gap-2 pr-8">
                      <h3 className="text-base font-bold text-gray-900 group-hover:text-[#655FA8] transition-colors line-clamp-1">
                        {project.name}
                      </h3>
                      {isDraft && (
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-200/80 px-2 py-0.5 rounded-full shrink-0">
                          Draft
                        </span>
                      )}
                    </div>
                    {!isDraft && project.created_at && (
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-400">
                        <Clock className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
                        <span>
                          <span className="font-bold text-gray-500">Created:</span>{' '}
                          {formatDate(project.created_at)}
                        </span>
                      </div>
                    )}
                  </div>

                  {!isDraft && (
                  <div className="flex flex-wrap gap-2 overflow-hidden">
                    {(project.markets || []).length > 0 ? (
                      (project.markets || []).map((market) => (
                        <span
                          key={market}
                          className="text-xs px-3 py-1 rounded-full border border-[#655FA8] text-[#655FA8] font-medium"
                        >
                          {market}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs px-3 py-1 rounded-full border border-[#655FA8] text-[#655FA8] font-medium">
                        Market(s)
                      </span>
                    )}
                    {project.opportunity_space ? (
                      <span className="text-xs px-3 py-1 rounded-full border border-[#655FA8] text-[#655FA8] font-medium line-clamp-1 max-w-[280px]">
                        {project.opportunity_space}
                      </span>
                    ) : project.goal ? (
                      <span className="text-xs px-3 py-1 rounded-full border border-[#655FA8] text-[#655FA8] font-medium line-clamp-1 max-w-[280px]">
                        {project.goal}
                      </span>
                    ) : (
                      <span className="text-xs px-3 py-1 rounded-full border border-[#655FA8] text-[#655FA8] font-medium">
                        Project Description
                      </span>
                    )}
                  </div>
                  )}
                </div>

                {!isDraft && project.status === 'in_process' && (
                  <div className="mx-5 mb-4 flex items-start gap-2.5 px-4 py-3 bg-[#E8F7FD] border border-[#43C8F5]/50 rounded-lg shrink-0">
                    <Info className="w-4 h-4 text-[#2A9CC4] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#2A9CC4]">In process</p>
                      <p className="text-xs text-[#2A9CC4] mt-0.5">
                        We will notify you when your project is ready.
                      </p>
                    </div>
                  </div>
                )}

                {!isDraft && project.status === 'jobs_identifier_creating' && (
                  <div className="mx-5 mb-4 flex items-start gap-2.5 px-4 py-3 bg-[#E8F7FD] border border-[#43C8F5]/50 rounded-lg shrink-0">
                    <Info className="w-4 h-4 text-[#2A9CC4] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#2A9CC4]">Jobs Identifier is being created</p>
                      <p className="text-xs text-[#2A9CC4] mt-0.5">
                        We will notify you when your project is ready.
                      </p>
                    </div>
                  </div>
                )}

                {!isDraft && project.status === 'outputs_ready' && canViewGos && (
                  <div className="mx-5 mb-4 flex items-start gap-2.5 px-4 py-3 bg-[#ECFDF3] border border-[#6EE7A0]/80 rounded-lg shrink-0">
                    <Check className="w-4 h-4 text-[#16A34A] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#16A34A]">Outputs ready</p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onViewGuaranteedOutputs) {
                            onViewGuaranteedOutputs(project.id);
                          } else {
                            onProjectSelect(project.id);
                          }
                        }}
                        className="text-xs text-[#16A34A] underline underline-offset-2 mt-0.5 hover:text-[#15803D] text-left"
                      >
                        View Guaranteed Outputs
                      </button>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {renameProject && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal
          aria-labelledby="rename-project-title"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-200">
              <div>
                <h3 id="rename-project-title" className="text-lg font-bold text-gray-900">
                  Rename project
                </h3>
                <p className="text-sm text-gray-500 mt-1">Update the project name.</p>
              </div>
              <button
                type="button"
                onClick={closeRenameModal}
                disabled={renameSaving}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleRenameSubmit} className="px-6 py-5">
              <label htmlFor="rename-project-name" className="block text-sm font-semibold text-gray-900 mb-2">
                Project name
              </label>
              <input
                id="rename-project-name"
                type="text"
                value={renameValue}
                onChange={(e) => {
                  setRenameValue(e.target.value);
                  if (renameError) setRenameError(null);
                }}
                className="w-full px-4 py-2.5 border border-[#E0DEE8] rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#43C8F5]/40 focus:border-[#43C8F5]"
                placeholder="Enter project name"
                disabled={renameSaving}
                autoFocus
              />
              {renameError && (
                <p className="mt-2 text-sm text-red-600">{renameError}</p>
              )}
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeRenameModal}
                  disabled={renameSaving}
                  className="px-4 py-2 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={renameSaving || !renameValue.trim()}
                  className="px-4 py-2 rounded-lg bg-[#655FA8] text-white font-medium hover:bg-[#544fa0] transition-colors disabled:opacity-50"
                >
                  {renameSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Delete project</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-gray-900">{deleteConfirm.project?.name}</span>?
              </p>
              <p className="text-sm text-gray-500">
                This action will archive the project and all its blueprints. This cannot be undone.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm({ show: false, project: null })}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-gray-700 font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSelectionView;
